import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode, RefObject } from "react";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import { useIsInStockContext } from "../../context/isInStock/IsInStockContext";

interface HeroGalleryVideoContextValue {
  videoUrl: string | null;
  isVideoAvailable: boolean;
  isVideoVisible: boolean;
  openVideo: () => void;
  closeVideo: () => void;
  triggerRef: RefObject<HTMLButtonElement>;
}

const HeroGalleryVideoContext =
  createContext<HeroGalleryVideoContextValue | null>(null);

export const HeroGalleryVideoProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { product } = useRuntimeConstants() as {
    product: { media?: { catwalkUrl: string | null } };
  };
  const { isInStock } = useIsInStockContext() as { isInStock: boolean | null };
  const videoUrl = product?.media?.catwalkUrl || null;

  const [isVideoVisible, setIsVideoVisible] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const openVideo = useCallback(() => {
    setIsVideoVisible(true);
  }, []);

  const closeVideo = useCallback(() => {
    setIsVideoVisible(false);
  }, []);

  const value = useMemo(
    () => ({
      videoUrl,
      isVideoAvailable: Boolean(videoUrl) && isInStock === true,
      isVideoVisible,
      openVideo,
      closeVideo,
      triggerRef,
    }),
    [videoUrl, isInStock, isVideoVisible, openVideo, closeVideo],
  );

  return (
    <HeroGalleryVideoContext.Provider value={value}>
      {children}
    </HeroGalleryVideoContext.Provider>
  );
};

export const useHeroGalleryVideo = (): HeroGalleryVideoContextValue => {
  const context = useContext(HeroGalleryVideoContext);

  if (!context) {
    throw new Error(
      "useHeroGalleryVideo must be used within a HeroGalleryVideoProvider",
    );
  }

  return context;
};
